import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { EventType } from "@/features/supply-config/event-types-api";

// A round-robin option row: managed-event name on top, then each team as a small
// pill/tag that also shows the event's type (unique slug), e.g. "Team · for-english".
// Left-aligned throughout.
function OptionRow({ et }: { et: EventType }) {
  const teams = et.teams ?? [];
  const typeSuffix = et.type ? ` · ${et.type}` : "";
  const pill =
    "inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-xs text-gray-700";
  return (
    <span className="flex flex-col gap-1 text-left min-w-0">
      {et.managed_event?.name && (
        <span className="text-sm text-gray-900">{et.managed_event.name}</span>
      )}
      <span className="flex flex-wrap items-center gap-1.5">
        {teams.length > 0
          ? teams.map((t) => (
              <span key={t.id} className={pill}>
                <Users className="h-3 w-3 flex-shrink-0" />
                {t.name}
                {typeSuffix}
              </span>
            ))
          : et.type && (
              <span className={pill}>
                <Users className="h-3 w-3 flex-shrink-0" />
                {et.type}
              </span>
            )}
      </span>
    </span>
  );
}

interface Props {
  eventTypes: EventType[];
  value: string | null; // selected eventTypeId
  onChange: (eventTypeId: string) => void;
}

export default function DecoratorRoundRobinPicker({
  eventTypes,
  value,
  onChange,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return eventTypes;
    return eventTypes.filter((et) => {
      if (et.managed_event?.name?.toLowerCase().includes(q)) return true;
      if (et.type?.toLowerCase().includes(q)) return true;
      if (et.teams?.some((t) => t.name.toLowerCase().includes(q))) return true;
      return false;
    });
  }, [eventTypes, query]);

  const selected = useMemo(
    () => eventTypes.find((et) => et.id === value) ?? null,
    [eventTypes, value],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-auto py-2 text-left"
        >
          {selected ? (
            <OptionRow et={selected} />
          ) : (
            <span className="text-sm text-muted-foreground">Select a team</span>
          )}
          <ChevronsUpDown className="h-4 w-4 opacity-50 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="p-2 border-b">
          <Input
            autoFocus
            placeholder="Filter by event, team, or type…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="max-h-72 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="p-3 text-sm text-gray-500">No round-robin events found.</p>
          ) : (
            filtered.map((et) => (
              <button
                key={et.id}
                type="button"
                onClick={() => {
                  onChange(et.id);
                  setOpen(false);
                }}
                className="flex w-full items-start justify-between gap-2 p-2 hover:bg-gray-50 text-left"
              >
                <OptionRow et={et} />
                {value === et.id && (
                  <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                )}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
